import { useState, useEffect, useRef, useMemo, useCallback, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Markdown, { type Components } from 'react-markdown';
import {
  ChevronLeft,
  ChevronDown,
  Send,
  Square,
  ImagePlus,
  Loader2,
  X,
  Bot,
  User,
  Copy,
  Check,
  AlertTriangle,
  ClipboardCheck,
  Brain,
  Database,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { llmApi, LlmAnnotation, LlmMessage, LlmUsableVectorStore, UploadedImage } from '@/api/llm';
import { useLlmSocket } from './hooks/useLlmSocket';
import { AuthImage } from './components/AuthImage';
import { ContextLimitPanel } from './components/ContextLimitPanel';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { TabBar } from '@/components/TabBar';
import {
  type LlmCitationSource,
  compactLlmCitationSnippet,
  formatLlmMessageCitations,
  formatStreamingLlmCitations,
  getLlmCitationSources,
  getLlmFileCitationNumber,
} from '@shared/types';

const TOKEN_LIMIT = 30000;
const TOKEN_WARNING_THRESHOLD = 25000;

export default function LlmChatPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();

  const [inputValue, setInputValue] = useState('');
  const [pendingImages, setPendingImages] = useState<UploadedImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [reasoningEffort, setReasoningEffort] = useState<string | undefined>(undefined);
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const [sourceSheetOpen, setSourceSheetOpen] = useState(false);
  const [selectedVectorStoreIds, setSelectedVectorStoreIds] = useState<string[]>([]);

  const {
    isConnected,
    isStreaming,
    streamingContent,
    reasoningContent,
    pendingUserMessage,
    sendMessage,
    stopStream,
    resetStreaming,
  } = useLlmSocket();

  // Fetch conversation with messages
  const { data: conversation, isLoading, isError, error } = useQuery({
    queryKey: ['llm-conversation', conversationId],
    queryFn: () => llmApi.getConversation(conversationId!),
    enabled: !!conversationId,
    refetchOnWindowFocus: false,
    retry: (failureCount, err: any) => {
      if (err?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });

  const { data: usableVectorStores, isLoading: isLoadingVectorStores } = useQuery({
    queryKey: ['llm-vector-stores-usable'],
    queryFn: () => llmApi.getUsableVectorStores(),
    enabled: !!conversation,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!conversation) return;
    setSelectedVectorStoreIds(
      (conversation.vectorStores || [])
        .filter((link) => link.enabled)
        .map((link) => link.storeId)
    );
  }, [conversation?.id, conversation?.vectorStores]);

  // Redirect on 404 (e.g. chat was soft-deleted)
  useEffect(() => {
    if (!isError) return;
    const status = (error as any)?.response?.status;
    if (status === 404) {
      toast.error('Чат не найден');
    } else {
      toast.error('Не удалось загрузить чат');
    }
    navigate('/more/chat', { replace: true });
  }, [isError, error, navigate]);

  // Auto-toggle reasoning panel: open when reasoning starts, close when text starts
  useEffect(() => {
    if (reasoningContent && !streamingContent) {
      setReasoningOpen(true);
    } else if (streamingContent) {
      setReasoningOpen(false);
    }
  }, [!!reasoningContent, !!streamingContent]);

  // Reset streaming state when navigating away
  useEffect(() => {
    return () => resetStreaming();
  }, [resetStreaming]);

  // Scroll to bottom when messages change or streaming starts/updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages, streamingContent, reasoningContent, isStreaming, pendingUserMessage]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [inputValue]);

  const handleSend = () => {
    if (!inputValue.trim() || !conversationId || isStreaming) return;

    const imageIds = pendingImages.map((img) => img.id);
    const readyStoreIds = new Set(
      (usableVectorStores?.items || [])
        .filter((store) => store.status === 'READY' && store.openaiVectorStoreId)
        .map((store) => store.id)
    );
    const vectorStoreIds = [
      ...new Set([
        ...selectedVectorStoreIds,
        ...requiredVectorStoreIds,
      ].filter((id) => readyStoreIds.has(id))),
    ];
    sendMessage(
      conversationId,
      inputValue.trim(),
      imageIds.length > 0 ? imageIds : undefined,
      reasoningEffort,
      vectorStoreIds
    );
    setInputValue('');
    setPendingImages([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) {
          toast.error('Ошибка', { description: 'Можно загружать только изображения' });
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error('Ошибка', { description: 'Файл слишком большой (макс. 10 МБ)' });
          continue;
        }
        const uploaded = await llmApi.uploadImage(file);
        setPendingImages((prev) => [...prev, uploaded]);
      }
    } catch (error: any) {
      toast.error('Ошибка', {
        description: error.response?.data?.message || 'Не удалось загрузить изображение',
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = (imageId: string) => {
    setPendingImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const messages = conversation?.messages || [];
  const vectorStores = usableVectorStores?.items || [];
  const requiredVectorStoreIds = useMemo(() => (
    (conversation?.preset?.vectorStores || [])
      .filter((link) => link.mode === 'REQUIRED')
      .map((link) => link.storeId)
  ), [conversation?.preset?.vectorStores]);
  const selectedVectorStores = vectorStores.filter((store) => (
    selectedVectorStoreIds.includes(store.id) || requiredVectorStoreIds.includes(store.id)
  ));

  const toggleVectorStore = (storeId: string, checked: boolean) => {
    if (requiredVectorStoreIds.includes(storeId)) return;
    setSelectedVectorStoreIds((prev) => {
      if (checked) return [...new Set([...prev, storeId])];
      return prev.filter((id) => id !== storeId);
    });
  };

  // Track messages with raw mode (markdown disabled)
  const [rawModeMessages, setRawModeMessages] = useState<Set<string>>(new Set());

  const toggleRawMode = useCallback((messageId: string) => {
    setRawModeMessages((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  }, []);

  // Calculate token usage from total content length (~4 chars per token)
  const currentTokens = useMemo(() => {
    if (!conversation?.messages) return 0;
    const totalChars = conversation.messages.reduce(
      (sum, msg) => sum + (msg.content?.length || 0), 0
    );
    return Math.round(totalChars / 4);
  }, [conversation?.messages]);

  const isNearLimit = currentTokens >= TOKEN_WARNING_THRESHOLD;
  const isAtLimit = currentTokens >= TOKEN_LIMIT;

  return (
    <>
      <div className="flex flex-col h-[100dvh] bg-background overflow-hidden pb-[var(--tab-bar-height)]">
        {/* Header */}
        <header className="flex items-center gap-2 px-4 py-3 border-b bg-card shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/more/chat')}
          className="shrink-0"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-semibold truncate">
            {conversation?.title || 'Новая беседа'}
          </h1>
          <p className="text-xs text-muted-foreground">
            {conversation?.preset?.name || conversation?.model}
          </p>
        </div>
        {!isConnected && (
          <span className="text-xs text-destructive">Нет связи</span>
        )}
      </header>

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 && !isStreaming ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Bot className="w-12 h-12 mb-2 opacity-50" />
            <p>Начните диалог</p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                rawMode={rawModeMessages.has(message.id)}
                onToggleRawMode={() => toggleRawMode(message.id)}
              />
            ))}
            {/* Pending user message (shown immediately before streaming response) */}
            {isStreaming && pendingUserMessage && (
              <div className="flex gap-3 flex-row-reverse">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                  <User className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col items-end">
                  <div className="rounded-lg rounded-tr-none p-3 max-w-[85%] bg-primary text-primary-foreground">
                    <p className="text-sm whitespace-pre-wrap break-words">{pendingUserMessage.content}</p>
                  </div>
                </div>
              </div>
            )}
            {/* Streaming message */}
            {isStreaming && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Bot className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  {/* Reasoning summary (collapsible) */}
                  {reasoningContent && (
                    <div className="mb-2">
                      <button
                        onClick={() => setReasoningOpen((o) => !o)}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Brain className="w-3.5 h-3.5" />
                        <span>{streamingContent ? 'Рассуждения' : 'Думаю...'}</span>
                        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', !reasoningOpen && '-rotate-90')} />
                      </button>
                      {(reasoningOpen || !streamingContent) && (
                        <div className="mt-1 pl-5 border-l-2 border-muted-foreground/20 text-xs text-muted-foreground prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
                          <Markdown components={markdownComponents}>{reasoningContent}</Markdown>
                          {!streamingContent && <span className="inline-block w-1.5 h-3 bg-muted-foreground/30 animate-pulse ml-0.5" />}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="bg-muted rounded-lg rounded-tl-none p-3">
                    {streamingContent ? (
                      <>
                        <p className="text-sm whitespace-pre-wrap">{formatStreamingLlmCitations(streamingContent)}</p>
                        <span className="inline-block w-2 h-4 bg-primary/50 animate-pulse ml-0.5" />
                      </>
                    ) : !reasoningContent ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Думаю...</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
        </div>

        {/* Input */}
        {!conversation ? null : isAtLimit ? (
          <ContextLimitPanel conversationId={conversationId!} />
        ) : (
          <div className="border-t bg-card shrink-0">
            {/* Warning banner */}
            {isNearLimit && (
              <div className="mx-3 mt-3 p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700">
                <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span className="text-sm">
                    Контекст заполнен на {Math.round((currentTokens / TOKEN_LIMIT) * 100)}%
                    ({currentTokens.toLocaleString()} / {TOKEN_LIMIT.toLocaleString()} токенов).
                    Скоро чат будет заблокирован.
                  </span>
                </div>
              </div>
            )}

            <div className="p-3">
              {/* Pending images */}
              {pendingImages.length > 0 && (
                <div className="flex gap-2 mb-2 overflow-x-auto pb-2">
                  {pendingImages.map((img) => (
                    <div key={img.id} className="relative shrink-0">
                      <AuthImage
                        src={llmApi.getImagePath(img.id)}
                        alt={img.originalName}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removeImage(img.id)}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Reasoning effort selector */}
              <div className="flex flex-wrap items-center gap-1 mb-2">
                <span className="text-xs text-muted-foreground mr-1">Reasoning:</span>
                {[
                  { value: undefined, label: 'Auto' },
                  { value: 'low', label: 'Low' },
                  { value: 'medium', label: 'Med' },
                  { value: 'high', label: 'High' },
                ].map((opt) => (
                  <button
                    key={opt.label}
                    onClick={() => setReasoningEffort(opt.value)}
                    className={cn(
                      'px-2 py-0.5 rounded text-xs transition-colors',
                      reasoningEffort === opt.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted-foreground/20'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setSourceSheetOpen(true)}
                  disabled={isStreaming}
                  className={cn(
                    'ml-1 inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs transition-colors',
                    selectedVectorStores.length > 0
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted-foreground/20',
                    isStreaming && 'cursor-not-allowed opacity-50'
                  )}
                  title="Хранилища знаний"
                >
                  <Database className="w-3.5 h-3.5" />
                  <span>Источники</span>
                  {selectedVectorStores.length > 0 && <span>{selectedVectorStores.length}</span>}
                </button>
              </div>

              <div className="flex items-end gap-2">
                {/* Image upload */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isStreaming || isUploading}
                  className="shrink-0"
                >
                  {isUploading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <ImagePlus className="w-5 h-5" />
                  )}
                </Button>

                {/* Text input */}
                <Textarea
                  ref={textareaRef}
                  placeholder="Введите сообщение..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isStreaming}
                  className="min-h-[40px] max-h-[120px] resize-none"
                  rows={1}
                />

                {/* Send/Stop button */}
                {isStreaming ? (
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={stopStream}
                    className="shrink-0"
                  >
                    <Square className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    size="icon"
                    onClick={handleSend}
                    disabled={!inputValue.trim() || !isConnected}
                    className="shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <VectorStoreSheet
        open={sourceSheetOpen}
        onOpenChange={setSourceSheetOpen}
        stores={vectorStores}
        selectedIds={selectedVectorStoreIds}
        requiredIds={requiredVectorStoreIds}
        isLoading={isLoadingVectorStores}
        onToggle={toggleVectorStore}
      />
      <TabBar />
    </>
  );
}

// Code block with copy button
function CodeBlock({ children, className }: { children: ReactNode; className?: string }) {
  const [copied, setCopied] = useState(false);

  // Extract language from className (e.g., "language-typescript")
  const language = className?.replace('language-', '') || '';

  // Get text content from children
  const getTextContent = (node: ReactNode): string => {
    if (typeof node === 'string') return node;
    if (Array.isArray(node)) return node.map(getTextContent).join('');
    if (node && typeof node === 'object' && 'props' in node) {
      return getTextContent((node.props as { children?: ReactNode }).children);
    }
    return '';
  };

  const code = getTextContent(children).trim();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  return (
    <div className="relative group/code my-2">
      {language && (
        <div className="absolute top-0 left-0 px-2 py-0.5 text-[10px] text-muted-foreground bg-muted rounded-br">
          {language}
        </div>
      )}
      <button
        onClick={handleCopy}
        className={cn(
          'absolute top-1 right-1 p-1.5 rounded bg-muted hover:bg-muted-foreground/20 transition-colors',
          'opacity-0 group-hover/code:opacity-100 focus:opacity-100',
          copied && 'text-green-500'
        )}
        title="Копировать код"
      >
        {copied ? (
          <ClipboardCheck className="w-3.5 h-3.5" />
        ) : (
          <Copy className="w-3.5 h-3.5" />
        )}
      </button>
      <pre className={cn(
        'overflow-x-auto p-3 rounded text-xs',
        'bg-zinc-100 dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100',
        language && 'pt-6'
      )}>
        <code className={className}>{children}</code>
      </pre>
    </div>
  );
}

// Inline code styling
function InlineCode({ children }: { children: ReactNode }) {
  return (
    <code className="px-1 py-0.5 rounded text-xs bg-zinc-200 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100">
      {children}
    </code>
  );
}

function SourcesList({
  annotations,
  onSelectCitation,
}: {
  annotations: LlmAnnotation[];
  onSelectCitation: (source: LlmCitationSource) => void;
}) {
  const [open, setOpen] = useState(false);

  const sources = useMemo(() => getLlmCitationSources(annotations), [annotations]);
  const urlSources = sources.filter((source) => source.type === 'url' && source.url);
  const fileSources = sources.filter((source) => source.type === 'file');

  const total = sources.length;
  if (total === 0) return null;

  const getDomain = (url: string) => {
    try { return new URL(url).hostname; } catch { return url; }
  };

  return (
    <div className="mt-1 max-w-[85%]">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <span>{total} {total === 1 ? 'источник' : total < 5 ? 'источника' : 'источников'}</span>
        <ChevronDown className={cn('w-3 h-3 transition-transform', !open && '-rotate-90')} />
      </button>
      {open && (
        <div className="mt-1 pl-2 border-l-2 border-muted-foreground/20 space-y-0.5">
          {urlSources.map((source) => {
            const url = source.url || '';
            return (
              <div key={url} className="text-xs">
                <span className="text-muted-foreground mr-1">{source.number}.</span>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {source.title}
                </a>
                <span className="text-muted-foreground ml-1">— {getDomain(url)}</span>
              </div>
            );
          })}
          {fileSources.map((source) => (
            <button
              key={`${source.number}:${source.title}`}
              type="button"
              onClick={() => onSelectCitation(source)}
              className="block w-full rounded px-1 py-1 text-left text-xs text-muted-foreground active:bg-accent active:text-foreground transition-colors"
            >
              <span className="text-muted-foreground mr-1">{source.number}.</span>
              <span>{source.title}</span>
              {source.snippet && (
                <span className="block truncate pl-4 text-[11px] text-muted-foreground/80">
                  {compactLlmCitationSnippet(source.snippet, 140)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function VectorStoreSheet({
  open,
  onOpenChange,
  stores,
  selectedIds,
  requiredIds,
  isLoading,
  onToggle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stores: LlmUsableVectorStore[];
  selectedIds: string[];
  requiredIds: string[];
  isLoading: boolean;
  onToggle: (storeId: string, checked: boolean) => void;
}) {
  const maintainerText = (store: LlmUsableVectorStore) => {
    if (!store.maintainers?.length) return null;
    return store.maintainers
      .map((item) => [item.employee.firstName, item.employee.lastName].filter(Boolean).join(' ').trim() || item.employee.email)
      .filter(Boolean)
      .join(', ');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[75vh]">
        <SheetHeader>
          <SheetTitle>Хранилища знаний</SheetTitle>
        </SheetHeader>
        <div className="mt-4 max-h-[55vh] overflow-y-auto space-y-1">
          {isLoading ? (
            <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Загрузка...</span>
            </div>
          ) : stores.length === 0 ? (
            <div className="px-2 py-3 text-sm text-muted-foreground">Нет готовых хранилищ</div>
          ) : (
            stores.map((store) => {
              const isReady = store.status === 'READY' && !!store.openaiVectorStoreId;
              const isRequired = requiredIds.includes(store.id);
              return (
                <label
                  key={store.id}
                  className={cn(
                    'flex items-start gap-3 rounded-lg px-2 py-3',
                    isReady ? 'active:bg-accent' : 'opacity-60'
                  )}
                >
                  <Checkbox
                    checked={(selectedIds.includes(store.id) || isRequired) && isReady}
                    disabled={!isReady || isRequired}
                    onCheckedChange={(checked) => onToggle(store.id, checked === true)}
                    className="mt-0.5"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{store.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {isRequired
                        ? 'Обязательный источник пресета'
                        : isReady
                          ? (store.description || `${store.fileCount} файлов`)
                          : `Не готово: ${store.status}`}
                    </span>
                    {maintainerText(store) && (
                      <span className="block text-xs text-muted-foreground/80">
                        Ответственные: {maintainerText(store)}
                      </span>
                    )}
                  </span>
                </label>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MessageVectorStores({ message }: { message: LlmMessage }) {
  if (!message.vectorStores || message.vectorStores.length === 0) return null;

  const names = message.vectorStores.map((link) => {
    const metadataName = link.metadata?.name;
    if (link.store?.name) return link.store.name;
    if (typeof metadataName === 'string' && metadataName.trim()) return metadataName;
    return link.openaiVectorStoreId;
  });

  return (
    <div className="mt-1 flex max-w-[85%] flex-wrap gap-1">
      {names.map((name) => (
        <span
          key={name}
          className="inline-flex items-center gap-1 rounded border border-border bg-muted/50 px-1.5 py-0.5 text-[11px] text-muted-foreground"
        >
          <Database className="w-3 h-3" />
          {name}
        </span>
      ))}
    </div>
  );
}

function FinishReasonBadge({ reason }: { reason: string }) {
  if (!reason || reason === 'stop' || reason === 'completed') return null;

  const labels: Record<string, string> = {
    length: '⚠️ Ответ обрезан из-за лимита токенов',
    content_filter: '⚠️ Ответ заблокирован фильтром контента',
    incomplete: '⚠️ Ответ не завершён',
  };

  const text = labels[reason] || `⚠️ Статус: ${reason}`;

  return (
    <div className="mt-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded inline-block">
      {text}
    </div>
  );
}

// Markdown components configuration
const markdownComponents: Components = {
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children }) => {
    const isCodeBlock = className?.startsWith('language-');
    const content = String(children);
    const isMultiline = content.includes('\n');

    if (isCodeBlock || isMultiline) {
      return <CodeBlock className={className}>{children}</CodeBlock>;
    }
    return <InlineCode>{children}</InlineCode>;
  },
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
      {children}
    </a>
  ),
};

function MessageMarkdown({
  content,
  annotations,
  onSelectCitation,
}: {
  content: string;
  annotations: LlmAnnotation[];
  onSelectCitation: (source: LlmCitationSource) => void;
}) {
  const citationSources = useMemo(() => getLlmCitationSources(annotations), [annotations]);
  const sourceByNumber = useMemo(
    () => new Map(citationSources.map((source) => [source.number, source])),
    [citationSources],
  );

  const components = useMemo<Components>(() => ({
    ...markdownComponents,
    a: ({ href, children }) => {
      const citationNumber = getLlmFileCitationNumber(href);
      if (citationNumber) {
        const source = sourceByNumber.get(citationNumber);
        return (
          <button
            type="button"
            disabled={!source}
            onTouchStart={(event) => event.stopPropagation()}
            onTouchEnd={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (source) onSelectCitation(source);
            }}
            className={cn(
              'inline-flex align-baseline rounded border border-primary/20 bg-primary/10 px-1 text-[0.85em] font-medium text-primary active:bg-primary/20',
              !source && 'cursor-default opacity-60',
            )}
            title={source?.snippet ? 'Показать фрагмент источника' : 'Фрагмент источника не вернулся'}
          >
            {children}
          </button>
        );
      }

      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
          {children}
        </a>
      );
    },
  }), [onSelectCitation, sourceByNumber]);

  return (
    <Markdown components={components}>
      {formatLlmMessageCitations(content, annotations, { linkFileCitations: true })}
    </Markdown>
  );
}

function CitationDetailsSheet({
  source,
  onClose,
}: {
  source: LlmCitationSource | null;
  onClose: () => void;
}) {
  const score = typeof source?.annotation.score === 'number'
    ? `${Math.round(source.annotation.score * 100)}%`
    : null;

  return (
    <Sheet open={!!source} onOpenChange={(open) => { if (!open) onClose(); }}>
      {source && (
        <SheetContent side="bottom" className="max-h-[82vh]">
          <SheetHeader className="pr-8 text-left">
            <SheetTitle>{source.title}</SheetTitle>
            <SheetDescription>
              Источник {source.number}
              {source.annotation.file_id ? ` • ${source.annotation.file_id}` : ''}
              {score ? ` • релевантность ${score}` : ''}
            </SheetDescription>
          </SheetHeader>
          {source.snippet ? (
            <div className="mt-4 max-h-[58vh] overflow-y-auto rounded border border-border bg-muted/50 p-3 text-sm leading-6 text-foreground whitespace-pre-wrap">
              {source.snippet}
            </div>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">
              OpenAI вернул ссылку на документ, но не вернул текстовый фрагмент для этой цитаты.
            </p>
          )}
        </SheetContent>
      )}
    </Sheet>
  );
}

interface ChatMessageProps {
  message: LlmMessage;
  rawMode?: boolean;
  onToggleRawMode?: () => void;
}

function useLongPress(callback: () => void, ms = 500) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggered = useRef(false);

  const start = useCallback(() => {
    triggered.current = false;
    timerRef.current = setTimeout(() => {
      triggered.current = true;
      callback();
    }, ms);
  }, [callback, ms]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Prevent click/context menu if long press was triggered
  const onClick = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (triggered.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  return {
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchMove: cancel,
    onContextMenu: (e: React.TouchEvent | React.MouseEvent) => { e.preventDefault(); },
    onClickCapture: onClick,
  };
}

function ChatMessage({ message, rawMode = false, onToggleRawMode }: ChatMessageProps) {
  const [copied, setCopied] = useState(false);
  const [selectedCitation, setSelectedCitation] = useState<LlmCitationSource | null>(null);
  const isUser = message.role === 'USER';
  const isSystem = message.role === 'SYSTEM';
  const isAssistant = message.role === 'ASSISTANT';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  const longPressHandlers = useLongPress(useCallback(() => {
    navigator.clipboard.writeText(message.content).then(
      () => toast.success('Скопировано'),
      () => toast.error('Не удалось скопировать'),
    );
  }, [message.content]));

  if (isSystem) {
    return (
      <div className="text-center">
        <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
          {message.content}
        </span>
      </div>
    );
  }

  return (
    <div className={cn('flex gap-3 group', isUser && 'flex-row-reverse')}>
      {isAssistant ? (
        <button
          onClick={onToggleRawMode}
          className={cn(
            'w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors',
            rawMode
              ? 'bg-muted-foreground/20 ring-2 ring-muted-foreground/30'
              : 'bg-primary/10 hover:bg-primary/20'
          )}
          title={rawMode ? 'Показать с форматированием' : 'Показать исходный текст'}
        >
          <Bot className={cn('w-4 h-4', rawMode ? 'text-muted-foreground' : 'text-primary')} />
        </button>
      ) : (
        <div
          className={cn(
            'w-8 h-8 rounded-full flex items-center justify-center shrink-0',
            'bg-primary text-primary-foreground'
          )}
        >
          <User className="w-4 h-4" />
        </div>
      )}
      <div className={cn('flex-1 min-w-0', isUser && 'flex flex-col items-end')}>
        {/* Images */}
        {message.images && message.images.length > 0 && (
          <div
            className={cn(
              'flex gap-2 mb-2 flex-wrap',
              isUser && 'justify-end'
            )}
          >
            {message.images.map((img) => (
              <AuthImage
                key={img.id}
                src={llmApi.getImagePath(img.id)}
                alt={img.originalName}
                className="max-w-[200px] max-h-[200px] object-cover rounded-lg"
              />
            ))}
          </div>
        )}

        {/* Message bubble */}
        <div
          {...longPressHandlers}
          className={cn(
            'rounded-lg p-3 max-w-[85%] select-none',
            isUser
              ? 'bg-primary text-primary-foreground rounded-tr-none'
              : 'bg-muted rounded-tl-none'
          )}
        >
          {isAssistant && !rawMode ? (
            <div className="text-sm prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
              <MessageMarkdown
                content={message.content}
                annotations={message.annotations || []}
                onSelectCitation={setSelectedCitation}
              />
            </div>
          ) : (
            <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
          )}
        </div>

        {/* Sources list */}
        {isAssistant && <MessageVectorStores message={message} />}

        {isAssistant && message.annotations && message.annotations.length > 0 && (
          <SourcesList annotations={message.annotations} onSelectCitation={setSelectedCitation} />
        )}

        {/* Finish reason warning */}
        {isAssistant && message.finishReason && (
          <FinishReasonBadge reason={message.finishReason} />
        )}

        {/* Metadata + copy button */}
        <div className={cn(
          'flex items-center gap-2 mt-1 text-xs text-muted-foreground',
          isUser && 'justify-end'
        )}>
          {!isUser && message.inputTokens && (
            <>
              <span>{message.inputTokens} in</span>
              <span>•</span>
              <span>{message.outputTokens} out</span>
              {message.durationMs && (
                <>
                  <span>•</span>
                  <span>{(message.durationMs / 1000).toFixed(1)}s</span>
                </>
              )}
              <span>•</span>
            </>
          )}
          <button
            onClick={handleCopy}
            className={cn(
              'p-1 rounded hover:bg-muted transition-colors',
              copied && 'text-green-500'
            )}
            title="Копировать"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
        <CitationDetailsSheet
          source={selectedCitation}
          onClose={() => setSelectedCitation(null)}
        />
      </div>
    </div>
  );
}
